<template>
    <div class="contents">
        <div class="container">
            <!-- <div class="row">
                <div class="col-lg-12">
                    <div class="breadcrumb-main">
                        <h4 class="text-capitalize breadcrumb-title">API Documentation</h4>
                    </div>
                </div>
            </div> -->

            <div class="row documentation-layout">
                <div class="col-lg-3 pr-lg-4">
                    <div class="toc-wrapper">
                        <div class="card shadow-lg toc-card sticky-wrapper" ref="tocCard">
                            <div class="card-body p-3">
                                <h5 class="mb-3 font-weight-bold text-dark border-bottom pb-2">Table of Contents</h5>
                                <div v-for="group in endpointGroups" :key="group.id" class="mb-3">
                                    <h6 class="text-uppercase text-muted font-weight-bold mb-2 toc-group-title">
                                        <i :class="group.icon + ' mr-2'"></i>{{ group.name }}
                                    </h6>
                                    <ul class="nav flex-column pl-0 mb-0">
                                        <li class="nav-item mb-1" v-for="endpoint in group.endpoints" :key="endpoint.path">
                                            <a :href="'#' + endpoint.id" class="nav-link toc-link" @click.prevent="scrollTo(endpoint.id)">
                                                <span :class="'badge badge-sm badge-' + getMethodColor(endpoint.method) + ' mr-2'">
                                                    {{ endpoint.method }}
                                                </span>
                                                <span class="endpoint-name">{{ endpoint.title }}</span>
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-lg-9 pl-lg-4">
                    <div class="card shadow-sm documentation-card mb-30">
                        <div class="card-body p-30">
                            <div class="mb-4">
                                <h3 class="mb-3">API Base URL</h3>
                                <div class="alert alert-info">
                                    <code>https://api.whacenter.com/api</code>
                                </div>
                            </div>

                            <div class="mb-4">
                                <h3 class="mb-3">Authentication</h3>
                                <p>All endpoints use <code>device_id</code> as authentication. The <code>device_id</code> acts as your API key and should be included in every request.</p>
                                <p class="mt-2">You can get your <code>device_id</code> from the Device management page after creating a device.</p>
                            </div>

                            <div class="mb-5 lid-guide-card">
                                <h3 class="mb-3">
                                    <i class="fas fa-id-card mr-2 text-primary"></i>
                                    LID &amp; WhatsApp Username
                                </h3>
                                <p class="text-muted">
                                    WhatsApp users who enable <strong>username</strong> or hide their phone number may send messages using a
                                    <strong>LID (Linked ID)</strong> instead of a regular phone number. WHACENTER automatically resolves LID to phone
                                    when possible and always provides fallback fields so you can still reply.
                                </p>

                                <div class="table-responsive mb-4">
                                    <table class="table table-sm table-bordered">
                                        <thead class="thead-light">
                                            <tr>
                                                <th>Identifier</th>
                                                <th>Example</th>
                                                <th>Description</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td><code>from</code></td>
                                                <td><code>081234567890</code></td>
                                                <td>Recipient phone (<code>0812...</code>) or bare LID when phone is hidden.</td>
                                            </tr>
                                            <tr>
                                                <td><code>lid</code></td>
                                                <td><code>12345678901234</code></td>
                                                <td>Bare LID user ID without <code>@lid</code> suffix.</td>
                                            </tr>
                                            <tr>
                                                <td><code>from_jid</code></td>
                                                <td><code>12345678901234@lid</code></td>
                                                <td>Full WhatsApp JID. Use as <code>number</code> when replying to LID users (e.g. <code>123...@lid</code>).</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <h6 class="font-weight-bold">Reply logic (recommended)</h6>
                                <ol class="pl-3 mb-4">
                                    <li>If <code>from</code> is not empty and looks like a phone number → send to <code>from</code></li>
                                    <li>If <code>from</code> is a bare LID (long numeric ID) → send to <code>{from}@lid</code> or use <code>from_jid</code></li>
                                    <li>Else if <code>from_jid</code> is not empty → send to <code>from_jid</code> as <code>number</code></li>
                                    <li>Else if <code>lid</code> is not empty → send to <code>{lid}@lid</code></li>
                                    <li>Optional: call <code>/api/convertLid</code> to try resolving LID to phone before sending</li>
                                </ol>

                                <h6 class="font-weight-bold">Example: reply from webhook payload</h6>
                                <pre class="code-block mb-0"><code>// Webhook received:
// { "from": "12345678901234", "lid": "12345678901234", "from_jid": "12345678901234@lid", ... }

const recipient = payload.from;

await fetch('https://api.whacenter.com/api/send', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    device_id: 'your_device_id',
    number: recipient,
    message: 'Terima kasih, pesan Anda sudah kami terima.'
  })
});</code></pre>
                            </div>

                            <div v-for="group in endpointGroups" :key="group.id" class="api-group mb-5">
                                <div class="group-header mb-4">
                                    <h2 class="mb-0">
                                        <i :class="group.icon + ' mr-2 text-primary'"></i>
                                        {{ group.name }}
                                    </h2>
                                    <p class="text-muted mt-2 mb-0">{{ group.description }}</p>
                                </div>
                                
                                <div v-for="endpoint in group.endpoints" :key="endpoint.path" :id="endpoint.id" class="endpoint-card mb-4">
                                    <div class="endpoint-header">
                                        <div class="d-flex align-items-center flex-wrap mb-2">
                                            <span :class="'badge badge-' + getMethodColor(endpoint.method) + ' mr-2 method-badge'">
                                                {{ endpoint.method }}
                                            </span>
                                            <code class="endpoint-path">{{ endpointUrl(endpoint.path) }}</code>
                                        </div>
                                        <h5 class="endpoint-title mb-2">{{ endpoint.title }}</h5>
                                        <p v-if="endpoint.description" class="endpoint-description mb-0">{{ endpoint.description }}</p>
                                    </div>

                                    <div class="endpoint-content">
                                        <div v-if="endpoint.notes && endpoint.notes.length" class="mb-4">
                                            <h6 class="section-title">
                                                <i class="fas fa-info-circle mr-2"></i>Notes
                                            </h6>
                                            <ul class="doc-notes-list mb-0">
                                                <li v-for="(note, noteIndex) in endpoint.notes" :key="noteIndex">{{ note }}</li>
                                            </ul>
                                        </div>

                                        <div v-if="endpoint.responseFields && endpoint.responseFields.length" class="mb-4">
                                            <h6 class="section-title">
                                                <i class="fas fa-table mr-2"></i>Response Fields
                                            </h6>
                                            <div class="table-responsive">
                                                <table class="table table-sm table-bordered table-hover">
                                                    <thead class="thead-light">
                                                        <tr>
                                                            <th>Field</th>
                                                            <th>Type</th>
                                                            <th>Description</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="field in endpoint.responseFields" :key="field.name">
                                                            <td><code class="param-name">{{ field.name }}</code></td>
                                                            <td><span class="badge badge-secondary">{{ field.type }}</span></td>
                                                            <td>{{ field.description }}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        <div class="endpoint-content-inner">
                                        <div v-if="endpoint.params && endpoint.params.length > 0" class="mb-4">
                                            <h6 class="section-title">
                                                <i class="fas fa-list-ul mr-2"></i>Parameters
                                            </h6>
                                            <div class="table-responsive">
                                                <table class="table table-sm table-bordered table-hover">
                                                    <thead class="thead-light">
                                                        <tr>
                                                            <th>Name</th>
                                                            <th>Type</th>
                                                            <th>Location</th>
                                                            <th>Required</th>
                                                            <th>Description</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="param in endpoint.params" :key="param.name">
                                                            <td><code class="param-name">{{ param.name }}</code></td>
                                                            <td><span class="badge badge-secondary">{{ param.type }}</span></td>
                                                            <td><span class="badge badge-info">{{ param.location }}</span></td>
                                                            <td>
                                                                <span v-if="param.required" class="badge badge-danger">Yes</span>
                                                                <span v-else class="badge badge-light">No</span>
                                                            </td>
                                                            <td>{{ param.description }}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        <div v-if="endpoint.body" class="mb-4">
                                            <h6 class="section-title">
                                                <i class="fas fa-code mr-2"></i>Request Body
                                            </h6>
                                            <pre class="code-block"><code>{{ JSON.stringify(endpoint.body, null, 2) }}</code></pre>
                                        </div>

                                        <div v-if="endpoint.response" class="mb-4">
                                            <h6 class="section-title">
                                                <i class="fas fa-reply mr-2"></i>{{ endpoint.responseTitle || 'Response' }}
                                            </h6>
                                            <pre class="code-block"><code>{{ JSON.stringify(endpoint.response, null, 2) }}</code></pre>
                                        </div>

                                        <div v-if="endpoint.errorResponse" class="mb-4">
                                            <h6 class="section-title">
                                                <i class="fas fa-exclamation-triangle mr-2"></i>Error Response
                                            </h6>
                                            <pre class="code-block"><code>{{ JSON.stringify(endpoint.errorResponse, null, 2) }}</code></pre>
                                        </div>

                                        <div v-if="endpoint.example" class="mb-4">
                                            <h6 class="section-title">
                                                <i class="fas fa-terminal mr-2"></i>Example Request
                                            </h6>
                                            <pre class="code-block"><code>{{ endpoint.example }}</code></pre>
                                        </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'Documentation',
    data() {
        return {
            baseURL: process.env.VUE_APP_PUBLIC_API_URL || process.env.VUE_APP_BASE_URL || 'https://api.whacenter.com',
            endpointGroups: [
                {
                    id: 'send-messages',
                    name: 'Send Messages',
                    icon: 'fas fa-paper-plane',
                    description: 'Endpoints for sending various types of messages',
                    endpoints: [
                        {
                            id: 'send-post',
                            method: 'POST',
                            path: '/api/send',
                            title: 'Send Message (POST)',
                            auth: false,
                            description: 'Send a text or media message to a phone number or LID. Use this endpoint to reply to incoming webhook messages.',
                            notes: [
                                'The number parameter accepts international phone (0812..., 62812..., 1415..., 44...) or a bare LID (13-15 digits, e.g. 5291701731569). @lid suffix is optional.',
                                'When replying to a hidden-phone user, pass the from value from the webhook as number (bare LID). The system tries to resolve to phone first, then sends via LID if the number is hidden.',
                                'Device must be connected (status CONNECTED) for the message to be delivered.'
                            ],
                            params: [
                                { name: 'device_id', type: 'string', location: 'body/query', required: true, description: 'Device ID (acts as API key)' },
                                { name: 'number', type: 'string', location: 'body/query', required: false, description: 'International phone (0812..., 62812..., 1415...) or bare LID (13-15 digits). @lid suffix optional. Required unless group is provided.' },
                                { name: 'message', type: 'string', location: 'body/query', required: false, description: 'Message text content' },
                                { name: 'file', type: 'string', location: 'body/query', required: false, description: 'Public URL of image/video/document to send (optional)' },
                                { name: 'schedule', type: 'string', location: 'body/query', required: false, description: 'Schedule delivery time (format: YYYY-MM-DD HH:mm:ss, optional)' }
                            ],
                            body: {
                                device_id: 'device123',
                                number: '081234567890',
                                message: 'Hello from API',
                                file: null,
                                schedule: null
                            },
                            response: {
                                Status: true,
                                Data: {
                                    message_id: 123
                                },
                                Message: 'success sent message'
                            },
                            example: `curl -X POST "\${this.baseURL}/api/send" \\
  -H "Content-Type: application/json" \\
  -d '{
    "device_id": "device123",
    "number": "081234567890",
    "message": "Hello from API"
  }'

# Reply to LID-only user (use bare LID from webhook "from"):
curl -X POST "\${this.baseURL}/api/send" \\
  -H "Content-Type: application/json" \\
  -d '{
    "device_id": "device123",
    "number": "12345678901234",
    "message": "Balasan untuk user username"
  }'`
                        },
                        {
                            id: 'send-get',
                            method: 'GET',
                            path: '/api/send',
                            title: 'Send Message (GET)',
                            auth: false,
                            description: 'Send a text or media message using GET query parameters. Supports phone numbers and bare LID.',
                            notes: [
                                'Same behavior as POST /api/send but all parameters are passed via query string.',
                                'Use URL encoding for message text containing special characters.'
                            ],
                            params: [
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID (acts as API key)' },
                                { name: 'number', type: 'string', location: 'query', required: false, description: 'Recipient phone (0812..., 62812...) or bare LID (12345678901234)' },
                                { name: 'message', type: 'string', location: 'query', required: false, description: 'Message text content' },
                                { name: 'file', type: 'string', location: 'query', required: false, description: 'Public file URL (optional)' },
                                { name: 'schedule', type: 'string', location: 'query', required: false, description: 'Schedule time (optional)' }
                            ],
                            response: {
                                Status: true,
                                Data: {
                                    message_id: 123
                                },
                                Message: 'success sent message'
                            },
                            example: `curl "\${this.baseURL}/api/send?device_id=device123&number=12345678901234&message=Hello"`
                        },
                        {
                            id: 'send-group-post',
                            method: 'POST',
                            path: '/api/sendGroup',
                            title: 'Send to Group (POST)',
                            auth: false,
                            description: 'Send message to a group using API key',
                            params: [
                                { name: 'device_id', type: 'string', location: 'body/query', required: true, description: 'Device ID' },
                                { name: 'group', type: 'string', location: 'body/query', required: true, description: 'Group name' },
                                { name: 'message', type: 'string', location: 'body/query', required: false, description: 'Message text' },
                                { name: 'file', type: 'string', location: 'body/query', required: false, description: 'File URL (optional)' },
                                { name: 'schedule', type: 'string', location: 'body/query', required: false, description: 'Schedule time' }
                            ],
                            body: {
                                device_id: 'device123',
                                group: 'My Group',
                                message: 'Hello group!',
                                file: null,
                                schedule: null
                            },
                            response: {
                                Status: true,
                                Data: {
                                    message_id: 123
                                },
                                Message: 'success sent message'
                            }
                        },
                        {
                            id: 'send-group-get',
                            method: 'GET',
                            path: '/api/sendGroup',
                            title: 'Send to Group (GET)',
                            auth: false,
                            description: 'Send message to a group using API key (GET method)',
                            params: [
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID' },
                                { name: 'group', type: 'string', location: 'query', required: true, description: 'Group name' },
                                { name: 'message', type: 'string', location: 'query', required: false, description: 'Message text' },
                                { name: 'file', type: 'string', location: 'query', required: false, description: 'File URL' },
                                { name: 'schedule', type: 'string', location: 'query', required: false, description: 'Schedule time' }
                            ],
                            response: {
                                Status: true,
                                Data: {
                                    message_id: 123
                                },
                                Message: 'success sent message'
                            }
                        },
                        {
                            id: 'send-location',
                            method: 'POST',
                            path: '/api/sendLocation',
                            title: 'Send Location',
                            auth: false,
                            description: 'Send location message using API key',
                            params: [
                                { name: 'device_id', type: 'string', location: 'body', required: true, description: 'Device ID' },
                                { name: 'number', type: 'string', location: 'body', required: false, description: 'Recipient phone (0812..., 62812...) or bare LID (12345678901234). Required if group is not provided.' },
                                { name: 'group', type: 'string', location: 'body', required: false, description: 'Group name (required if number not provided)' },
                                { name: 'latitude', type: 'string', location: 'body', required: true, description: 'Latitude coordinate' },
                                { name: 'longitude', type: 'string', location: 'body', required: true, description: 'Longitude coordinate' },
                                { name: 'schedule', type: 'string', location: 'body', required: false, description: 'Schedule time' }
                            ],
                            body: {
                                device_id: 'device123',
                                number: '081234567890',
                                latitude: '-6.2088',
                                longitude: '106.8456',
                                schedule: null
                            },
                            response: {
                                Status: true,
                                Data: {
                                    id: 123
                                },
                                Message: 'success sent message'
                            }
                        },
                        {
                            id: 'send-poll',
                            method: 'POST',
                            path: '/api/sendPoll',
                            title: 'Send Poll to Number',
                            auth: false,
                            description: 'Send poll message to a number using API key',
                            params: [
                                { name: 'device_id', type: 'string', location: 'body', required: true, description: 'Device ID' },
                                { name: 'number', type: 'string', location: 'body', required: false, description: 'Recipient phone (0812..., 62812...) or bare LID (12345678901234). Required if group is not provided.' },
                                { name: 'question', type: 'string', location: 'body', required: true, description: 'Poll question' },
                                { name: 'options', type: 'array', location: 'body', required: true, description: 'Array of poll options' },
                                { name: 'max_answer', type: 'integer', location: 'body', required: true, description: 'Maximum number of answers allowed' },
                                { name: 'schedule', type: 'string', location: 'body', required: false, description: 'Schedule time' }
                            ],
                            body: {
                                device_id: 'device123',
                                number: '081234567890',
                                question: 'What is your favorite color?',
                                options: ['Red', 'Blue', 'Green'],
                                max_answer: 1,
                                schedule: null
                            },
                            response: {
                                Status: true,
                                Data: {
                                    id: 123
                                },
                                Message: 'success sent message'
                            }
                        },
                        {
                            id: 'send-poll-group',
                            method: 'POST',
                            path: '/api/sendPollGroup',
                            title: 'Send Poll to Group',
                            auth: false,
                            description: 'Send poll message to a group using API key',
                            params: [
                                { name: 'device_id', type: 'string', location: 'body', required: true, description: 'Device ID' },
                                { name: 'group', type: 'string', location: 'body', required: false, description: 'Group name (required if number not provided)' },
                                { name: 'question', type: 'string', location: 'body', required: true, description: 'Poll question' },
                                { name: 'options', type: 'array', location: 'body', required: true, description: 'Array of poll options' },
                                { name: 'max_answer', type: 'integer', location: 'body', required: true, description: 'Maximum number of answers allowed' },
                                { name: 'schedule', type: 'string', location: 'body', required: false, description: 'Schedule time' }
                            ],
                            body: {
                                device_id: 'device123',
                                group: 'My Group',
                                question: 'What is your favorite color?',
                                options: ['Red', 'Blue', 'Green'],
                                max_answer: 1,
                                schedule: null
                            },
                            response: {
                                Status: true,
                                Data: {
                                    id: 123
                                },
                                Message: 'success sent message'
                            }
                        },
                    ]
                },
                {
                    id: 'device-management',
                    name: 'Device Management',
                    icon: 'fas fa-mobile-alt',
                    description: 'Endpoints for managing and monitoring devices',
                    endpoints: [
                        {
                            id: 'get-qr',
                            method: 'GET',
                            path: '/api/qr',
                            title: 'Get QR Code',
                            auth: false,
                            description: 'Get device QR code as PNG image',
                            params: [
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID' }
                            ],
                            response: {
                                description: 'Returns PNG image of QR code',
                                contentType: 'image/png'
                            }
                        },
                        {
                            id: 'status-device',
                            method: 'GET',
                            path: '/api/statusDevice',
                            title: 'Get Device Status',
                            auth: false,
                            description: 'Get device status and information',
                            params: [
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID' }
                            ],
                            response: {
                                Status: true,
                                Data: {
                                    status: 'CONNECTED',
                                    nomor: '081234567890',
                                    nama: 'My Device',
                                    qr: 'data:image/png;base64,...'
                                },
                                Message: 'success get device status'
                            }
                        },
                        {
                            id: 'relog-device-get',
                            method: 'GET',
                            path: '/api/relogDevice',
                            title: 'Relog Device (GET)',
                            auth: false,
                            description: 'Relog/restart device connection (GET method)',
                            params: [
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID' }
                            ],
                            response: {
                                Status: true,
                                Data: {},
                                Message: 'berhasil relog device'
                            }
                        },
                        {
                            id: 'relog-device-post',
                            method: 'POST',
                            path: '/api/relogDevice',
                            title: 'Relog Device (POST)',
                            auth: false,
                            description: 'Relog/restart device connection (POST method)',
                            params: [
                                { name: 'device_id', type: 'string', location: 'body/query', required: true, description: 'Device ID' }
                            ],
                            body: {
                                device_id: 'device123'
                            },
                            response: {
                                Status: true,
                                Data: {},
                                Message: 'berhasil relog device'
                            }
                        },
                        {
                            id: 'add-device-get',
                            method: 'GET',
                            path: '/api/addDevice',
                            title: 'Add Device (GET)',
                            auth: false,
                            description: 'Create a new device (GET method)',
                            params: [
                                { name: 'api_key', type: 'string', location: 'query', required: true, description: 'API Key' },
                                { name: 'name', type: 'string', location: 'query', required: true, description: 'Device name' },
                                { name: 'number', type: 'string', location: 'query', required: true, description: 'Phone number' }
                            ],
                            response: {
                                Status: true,
                                Data: {
                                    device: {
                                        id: 123,
                                        device_id: 'device123',
                                        name: 'My Device',
                                        number: '081234567890',
                                        status: 'NOT CONNECTED'
                                    }
                                },
                                Message: 'Device created'
                            }
                        },
                        {
                            id: 'add-device-post',
                            method: 'POST',
                            path: '/api/addDevice',
                            title: 'Add Device (POST)',
                            auth: false,
                            description: 'Create a new device (POST method)',
                            params: [
                                { name: 'api_key', type: 'string', location: 'body', required: true, description: 'API Key' },
                                { name: 'name', type: 'string', location: 'body', required: true, description: 'Device name' },
                                { name: 'number', type: 'string', location: 'body', required: true, description: 'Phone number' }
                            ],
                            body: {
                                api_key: 'your_api_key',
                                name: 'My Device',
                                number: '081234567890'
                            },
                            response: {
                                Status: true,
                                Data: {
                                    device: {
                                        id: 123,
                                        device_id: 'device123',
                                        name: 'My Device',
                                        number: '081234567890',
                                        status: 'NOT CONNECTED'
                                    }
                                },
                                Message: 'Device created'
                            }
                        },
                        {
                            id: 'delete-device',
                            method: 'GET',
                            path: '/api/deleteDevice',
                            title: 'Delete Device',
                            auth: false,
                            description: 'Delete a device by ID',
                            params: [
                                { name: 'api_key', type: 'string', location: 'query', required: true, description: 'API Key' },
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID to delete' }
                            ],
                            response: {
                                Status: true,
                                Data: {},
                                Message: 'Device deleted successfully'
                            }
                        },
                        {
                            id: 'logout-device-get',
                            method: 'GET',
                            path: '/api/logoutDevice',
                            title: 'Logout Device (GET)',
                            auth: false,
                            description: 'Logout/disconnect a device (GET method)',
                            params: [
                                { name: 'api_key', type: 'string', location: 'query', required: true, description: 'API Key' },
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID' }
                            ],
                            response: {
                                Status: true,
                                Data: {},
                                Message: 'Device logout success'
                            }
                        },
                        {
                            id: 'logout-device-post',
                            method: 'POST',
                            path: '/api/logoutDevice',
                            title: 'Logout Device (POST)',
                            auth: false,
                            description: 'Logout/disconnect a device (POST method)',
                            params: [
                                { name: 'api_key', type: 'string', location: 'body', required: true, description: 'API Key' },
                                { name: 'device_id', type: 'string', location: 'body', required: true, description: 'Device ID' }
                            ],
                            body: {
                                api_key: 'your_api_key',
                                device_id: 'device123'
                            },
                            response: {
                                Status: true,
                                Data: {},
                                Message: 'Device logout success'
                            }
                        },
                    ]
                },
                {
                    id: 'webhook-management',
                    name: 'Webhook Management',
                    icon: 'fas fa-link',
                    description: 'Endpoints for managing webhook URLs',
                    endpoints: [
                        {
                            id: 'set-webhook-get',
                            method: 'GET',
                            path: '/api/setWebhook',
                            title: 'Set Webhook (GET)',
                            auth: false,
                            description: 'Set webhook URL for a device (GET method)',
                            params: [
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID' },
                                { name: 'webhook', type: 'string', location: 'query', required: false, description: 'Webhook URL' }
                            ],
                            response: {
                                Status: true,
                                Data: {},
                                Message: 'Success to set webhook'
                            }
                        },
                        {
                            id: 'set-webhook-post',
                            method: 'POST',
                            path: '/api/setWebhook',
                            title: 'Set Webhook (POST)',
                            auth: false,
                            description: 'Set webhook URL for a device (POST method)',
                            params: [
                                { name: 'device_id', type: 'string', location: 'body', required: true, description: 'Device ID' },
                                { name: 'webhook', type: 'string', location: 'body', required: false, description: 'Webhook URL' }
                            ],
                            body: {
                                device_id: 'device123',
                                webhook: 'https://example.com/webhook'
                            },
                            response: {
                                Status: true,
                                Data: {},
                                Message: 'Success to set webhook'
                            }
                        },
                        {
                            id: 'get-webhook-get',
                            method: 'GET',
                            path: '/api/getWebhook',
                            title: 'Get Webhook (GET)',
                            auth: false,
                            description: 'Get webhook URL for a device (GET method)',
                            params: [
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID' }
                            ],
                            response: {
                                Status: true,
                                Data: {
                                    webhook: 'https://example.com/webhook'
                                },
                                Message: 'Success to set webhook'
                            }
                        },
                        {
                            id: 'get-webhook-post',
                            method: 'POST',
                            path: '/api/getWebhook',
                            title: 'Get Webhook (POST)',
                            auth: false,
                            description: 'Get webhook URL for a device (POST method)',
                            params: [
                                { name: 'device_id', type: 'string', location: 'body', required: true, description: 'Device ID' }
                            ],
                            body: {
                                device_id: 'device123'
                            },
                            response: {
                                Status: true,
                                Data: {
                                    webhook: 'https://example.com/webhook'
                                },
                                Message: 'Success to set webhook'
                            }
                        }
                    ]
                },
                {
                    id: 'group-management',
                    name: 'Group Management',
                    icon: 'fas fa-users',
                    description: 'Endpoints for managing WhatsApp groups',
                    endpoints: [
                        {
                            id: 'get-group',
                            method: 'GET',
                            path: '/api/getGroup',
                            title: 'Get Group List',
                            auth: false,
                            description: 'Get list of all groups for a device',
                            params: [
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID' }
                            ],
                            response: {
                                Status: true,
                                Data: {
                                    groups: [
                                        {
                                            id: '120363123456789012@g.us',
                                            name: 'My Group',
                                            participants: 10
                                        }
                                    ]
                                },
                                Message: 'Success get group'
                            }
                        },
                        {
                            id: 'add-number-to-group',
                            method: 'POST',
                            path: '/api/addNumberToGroup',
                            title: 'Add Number to Group',
                            auth: false,
                            description: 'Add one or more numbers to a WhatsApp group',
                            params: [
                                { name: 'api_key', type: 'string', location: 'body', required: true, description: 'API Key' },
                                { name: 'group_id', type: 'integer', location: 'body', required: true, description: 'Group ID from database' },
                                { name: 'data', type: 'array', location: 'body', required: true, description: 'Array of phone numbers (strings)' }
                            ],
                            body: {
                                api_key: 'your_api_key',
                                group_id: 123,
                                data: [
                                    '081234567890',
                                    '081234567891',
                                    '081234567892'
                                ]
                            },
                            response: {
                                Status: true,
                                Data: {},
                                Message: 'Numbers added to group successfully'
                            }
                        }
                    ]
                },
                {
                    id: 'incoming-webhook',
                    name: 'Incoming Webhook',
                    icon: 'fas fa-inbox',
                    description: 'WHACENTER sends a POST request to your configured webhook URL every time a message is received on a connected device.',
                    endpoints: [
                        {
                            id: 'incoming-webhook-payload',
                            method: 'POST',
                            path: 'Your webhook URL (configured via /api/setWebhook)',
                            title: 'Incoming Message Payload',
                            auth: false,
                            description: 'JSON payload delivered to your server when someone sends a message to your WhatsApp device. For users with WhatsApp username or hidden phone number, the system resolves the phone when possible and always includes LID fallback fields.',
                            notes: [
                                'Configure webhook URL per device using GET/POST /api/setWebhook.',
                                'Your server must respond with HTTP 2xx. WHACENTER does not retry failed webhook deliveries automatically.',
                                'The from field is the sender phone (0812...) when WhatsApp exposes it. If the phone is hidden, from contains the bare LID instead of an empty string.',
                                'from_jid and lid are always included for LID/username users so you can reply reliably.',
                                'message_type values: text, media, location, live-location, poll.',
                                'For group messages, is_group is true and id_group contains the group JID (e.g. 120363...@g.us).'
                            ],
                            responseFields: [
                                { name: 'pushName', type: 'string', description: 'Display name of the sender (may be "username" literal for hidden-name users)' },
                                { name: 'from', type: 'string', description: 'Sender phone in local format (0812...). If phone is hidden, contains bare LID (e.g. 12345678901234).' },
                                { name: 'lid', type: 'string', description: 'Bare LID identifier without @lid suffix. Present when sender uses LID addressing.' },
                                { name: 'from_jid', type: 'string', description: 'Full sender JID (phone@s.whatsapp.net or lid@lid). Use as number when replying to LID users, e.g. 12345678901234@lid.' },
                                { name: 'to', type: 'string', description: 'Your device phone number (recipient of the incoming message)' },
                                { name: 'message_type', type: 'string', description: 'Type of message: text, media, location, live-location, poll' },
                                { name: 'message', type: 'string', description: 'Text content. For location: "lat,long". For media: caption text or empty.' },
                                { name: 'media', type: 'string', description: 'Public URL to downloaded media file. Empty for text messages.' },
                                { name: 'is_group', type: 'boolean', description: 'true if message was sent in a group chat' },
                                { name: 'timestamp', type: 'string', description: 'Message time in Asia/Jakarta timezone (YYYY-MM-DD HH:mm:ss)' },
                                { name: 'id_group', type: 'string|null', description: 'Group JID when is_group is true, otherwise null' },
                                { name: 'source', type: 'string', description: 'Always "WHACENTER"' },
                                { name: 'ad_reply', type: 'object', description: 'Facebook/Instagram ad reply metadata (source_type, source_id, source_url) when applicable' }
                            ],
                            body: {
                                pushName: 'John',
                                from: '081234567890',
                                lid: '12345678901234',
                                from_jid: '12345678901234@lid',
                                to: '628999999999',
                                message_type: 'text',
                                message: 'Hello',
                                media: '',
                                is_group: false,
                                timestamp: '2026-08-10 23:00:00',
                                id_group: null,
                                source: 'WHACENTER',
                                ad_reply: {
                                    source_type: null,
                                    source_id: null,
                                    source_url: null
                                }
                            },
                            response: {
                                description: 'Your server should return any HTTP 2xx status code to acknowledge receipt.'
                            },
                            example: `# Scenario 1: Normal user (phone visible)
{
  "pushName": "Budi",
  "from": "081234567890",
  "lid": "98765432109876",
  "from_jid": "628123456789@s.whatsapp.net",
  "to": "628999999999",
  "message_type": "text",
  "message": "Halo",
  "media": "",
  "is_group": false,
  "timestamp": "2026-08-10 14:30:00",
  "id_group": null,
  "source": "WHACENTER"
}

# Scenario 2: Username / hidden phone user (from contains LID)
{
  "pushName": "username",
  "from": "12345678901234",
  "lid": "12345678901234",
  "from_jid": "12345678901234@lid",
  "to": "628999999999",
  "message_type": "text",
  "message": "Pesan dari user username",
  "media": "",
  "is_group": false,
  "timestamp": "2026-08-10 14:31:00",
  "id_group": null,
  "source": "WHACENTER"
}

# Reply to Scenario 2:
POST \${this.baseURL}/api/send
{ "device_id": "...", "number": "12345678901234", "message": "Halo kembali!" }`
                        },
                        {
                            id: 'incoming-webhook-media',
                            method: 'POST',
                            path: 'Your webhook URL',
                            title: 'Incoming Media Message Payload',
                            auth: false,
                            description: 'Example payload when the sender sends an image, video, or document.',
                            body: {
                                pushName: 'Ani',
                                from: '081234567891',
                                lid: '',
                                from_jid: '6281234567891@s.whatsapp.net',
                                to: '628999999999',
                                message_type: 'media',
                                message: 'Ini caption gambar',
                                media: 'https://api.whacenter.com/api/media?path=ABC123.jpg',
                                is_group: false,
                                timestamp: '2026-08-10 14:32:00',
                                id_group: null,
                                source: 'WHACENTER'
                            }
                        },
                        {
                            id: 'incoming-webhook-group',
                            method: 'POST',
                            path: 'Your webhook URL',
                            title: 'Incoming Group Message Payload',
                            auth: false,
                            description: 'Example payload for a group message. The from field is the sender; id_group is the group JID.',
                            body: {
                                pushName: 'Rina',
                                from: '081234567892',
                                lid: '11122233344455',
                                from_jid: '6281234567892@s.whatsapp.net',
                                to: '628999999999',
                                message_type: 'text',
                                message: 'Halo grup',
                                media: '',
                                is_group: true,
                                timestamp: '2026-08-10 14:33:00',
                                id_group: '120363123456789012@g.us',
                                source: 'WHACENTER'
                            }
                        }
                    ]
                },
                {
                    id: 'utilities',
                    name: 'Utilities',
                    icon: 'fas fa-tools',
                    description: 'Helper endpoints for identifier conversion and troubleshooting LID/username issues.',
                    endpoints: [
                        {
                            id: 'convert-lid-get',
                            method: 'GET',
                            path: '/api/convertLid',
                            title: 'Convert LID to Phone (GET)',
                            auth: false,
                            description: 'Resolve a WhatsApp LID (Linked ID) to a phone number using the device internal LID mapping store. Useful when you received a lid in webhook and want the actual phone number for CRM or database storage.',
                            notes: [
                                'Device must be connected and must have previously interacted with the contact (chat history, group, or contact sync).',
                                'The lid parameter accepts bare ID (12345678901234) or full JID (12345678901234@lid). Bare input is auto-suffixed with @lid.',
                                'If mapping is not found, the API returns Status: false. In that case, reply using from_jid from webhook instead.',
                                'Conversion is best-effort. Users with fully hidden phone numbers may never resolve to a phone.'
                            ],
                            params: [
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID (acts as API key)' },
                                { name: 'lid', type: 'string', location: 'query', required: true, description: 'LID value: bare number (12345678901234) or full JID (12345678901234@lid)' }
                            ],
                            responseFields: [
                                { name: 'Status', type: 'boolean', description: 'true on success, false on error' },
                                { name: 'Data.lid', type: 'string', description: 'Normalized LID JID (e.g. 12345678901234@lid)' },
                                { name: 'Data.phone', type: 'string', description: 'Phone in local format (0812...)' },
                                { name: 'Data.jid', type: 'string', description: 'Full phone JID (62812...@s.whatsapp.net)' },
                                { name: 'Message', type: 'string', description: 'Human-readable result message' }
                            ],
                            response: {
                                Status: true,
                                Data: {
                                    lid: '12345678901234@lid',
                                    phone: '081234567890',
                                    jid: '628123456789@s.whatsapp.net'
                                },
                                Message: 'LID converted successfully'
                            },
                            errorResponse: {
                                Status: false,
                                Data: {
                                    lid: '12345678901234@lid'
                                },
                                Message: 'LID mapping not found'
                            },
                            example: `curl "\${this.baseURL}/api/convertLid?device_id=device123&lid=12345678901234"

# Also works with full JID:
curl "\${this.baseURL}/api/convertLid?device_id=device123&lid=12345678901234@lid"`
                        },
                        {
                            id: 'convert-lid-post',
                            method: 'POST',
                            path: '/api/convertLid',
                            title: 'Convert LID to Phone (POST)',
                            auth: false,
                            description: 'Same as GET /api/convertLid but accepts parameters in JSON body.',
                            notes: [
                                'Prefer POST when integrating from backend services to avoid URL length limits.',
                                'Same resolution rules and limitations as the GET variant.'
                            ],
                            params: [
                                { name: 'device_id', type: 'string', location: 'body', required: true, description: 'Device ID (acts as API key)' },
                                { name: 'lid', type: 'string', location: 'body', required: true, description: 'LID value: bare number or full @lid JID' }
                            ],
                            body: {
                                device_id: 'device123',
                                lid: '12345678901234'
                            },
                            response: {
                                Status: true,
                                Data: {
                                    lid: '12345678901234@lid',
                                    phone: '081234567890',
                                    jid: '628123456789@s.whatsapp.net'
                                },
                                Message: 'LID converted successfully'
                            },
                            errorResponse: {
                                Status: false,
                                Data: {
                                    lid: '12345678901234'
                                },
                                Message: 'LID mapping not found'
                            },
                            example: `curl -X POST "\${this.baseURL}/api/convertLid" \\
  -H "Content-Type: application/json" \\
  -d '{
    "device_id": "device123",
    "lid": "12345678901234"
  }'`
                        }
                    ]
                },
                {
                    id: 'message-management',
                    name: 'Message Management',
                    icon: 'fas fa-envelope',
                    description: 'Endpoints for managing messages and retrieving data',
                    endpoints: [
                        {
                            id: 'delete-message',
                            method: 'GET',
                            path: '/api/deleteMessage',
                            title: 'Delete Message',
                            auth: false,
                            description: 'Delete a message by ID',
                            params: [
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID' },
                                { name: 'id', type: 'string', location: 'query', required: true, description: 'Message ID to delete' }
                            ],
                            response: {
                                Status: true,
                                Data: {},
                                Message: 'hapus pesan sukses'
                            }
                        },
                        {
                            id: 'get-poll',
                            method: 'GET',
                            path: '/api/poll',
                            title: 'Get Poll Results',
                            auth: false,
                            description: 'Get poll results by message ID',
                            params: [
                                { name: 'msg_id', type: 'string', location: 'query', required: true, description: 'Message ID of the poll' }
                            ],
                            response: {
                                Status: true,
                                Data: {
                                    results: [
                                        {
                                            id: 'abc123...',
                                            name: 'Red',
                                            voters: ['081234567890', '081234567891'],
                                            count: 2
                                        },
                                        {
                                            id: 'def456...',
                                            name: 'Blue',
                                            voters: ['081234567892'],
                                            count: 1
                                        }
                                    ],
                                    winner: {
                                        id: 'abc123...',
                                        name: 'Red',
                                        voters: ['081234567890', '081234567891'],
                                        count: 2
                                    }
                                },
                                Message: 'Success to get poll results'
                            }
                        },
                        {
                            id: 'get-media',
                            method: 'GET',
                            path: '/api/media',
                            title: 'Get Media File',
                            auth: false,
                            description: 'Get media file by message ID',
                            params: [
                                { name: 'msg_id', type: 'string', location: 'query', required: true, description: 'Message ID containing media' }
                            ],
                            response: {
                                description: 'Returns media file (image, video, audio, document)',
                                contentType: 'varies based on media type'
                            }
                        }
                    ]
                }
            ]
        }
    },
    computed: {
        publicApiURL() {
            let url = String(this.baseURL || 'https://api.whacenter.com').replace(/\/$/, '');
            url = url.replace(/\/api\/v1$/, '');
            if (url.endsWith('/api')) {
                url = url.slice(0, -4);
            }
            return url;
        }
    },
    methods: {
        endpointUrl(path) {
            if (!path || !path.startsWith('/')) {
                return path;
            }
            return this.publicApiURL + path;
        },
        getMethodColor(method) {
            const colors = {
                'GET': 'info',
                'POST': 'success',
                'PUT': 'warning',
                'DELETE': 'danger',
                'PATCH': 'secondary'
            };
            return colors[method] || 'secondary';
        },
        scrollTo(id) {
            const element = document.getElementById(id);
            if (element) {
                const offset = 100;
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - offset;
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }
        },
        updateTocPosition() {
            this.$nextTick(() => {
                if (this.$refs.tocCard) {
                    const tocWrapper = this.$refs.tocCard.closest('.col-lg-3');
                    if (tocWrapper && window.innerWidth >= 992) {
                        const rect = tocWrapper.getBoundingClientRect();
                        const container = tocWrapper.closest('.container');
                        if (container) {
                            const containerRect = container.getBoundingClientRect();
                            // Calculate left position relative to viewport
                            const left = containerRect.left + (rect.left - containerRect.left);
                            
                            // Apply styles
                            this.$refs.tocCard.style.position = 'fixed';
                            this.$refs.tocCard.style.left = left + 'px';
                            this.$refs.tocCard.style.width = rect.width + 'px';
                            this.$refs.tocCard.style.top = '100px';
                            this.$refs.tocCard.style.zIndex = '10';
                        }
                    } else {
                        // Reset for mobile
                        if (this.$refs.tocCard) {
                            this.$refs.tocCard.style.position = 'relative';
                            this.$refs.tocCard.style.left = 'auto';
                            this.$refs.tocCard.style.width = 'auto';
                            this.$refs.tocCard.style.top = 'auto';
                        }
                    }
                }
            });
        }
    },
    mounted() {
        // Update baseURL in examples
        this.endpointGroups.forEach(group => {
            group.endpoints.forEach(endpoint => {
                if (endpoint.example) {
                    endpoint.example = endpoint.example.replace('${this.baseURL}', this.publicApiURL);
                }
            });
        });
        
        // Set fixed position and width for TOC
        this.$nextTick(() => {
            this.updateTocPosition();
            window.addEventListener('resize', this.updateTocPosition);
        });
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateTocPosition);
    }
}
</script>

<style scoped>
.lid-guide-card {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-left: 4px solid #007bff;
    border-radius: 8px;
    padding: 24px;
}

.doc-notes-list li {
    margin-bottom: 8px;
    color: #495057;
}

.doc-notes-list li:last-child {
    margin-bottom: 0;
}

.contents {
    overflow: visible !important;
    padding: 20px 0;
}

.container {
    overflow: visible !important;
    max-width: 1400px;
}

.documentation-layout {
    overflow: visible !important;
    margin-top: 5px;
    align-items: flex-start;
    position: relative;
}

.col-lg-3 {
    position: relative;
    overflow: visible !important;
    z-index: 1;
}

.col-lg-9 {
    position: relative;
    overflow: visible !important;
    z-index: 2;
}

.api-group {
    scroll-margin-top: 100px;
    background: #f8f9fa;
    padding: 2rem;
    border-radius: 12px;
    margin-bottom: 2.5rem;
}

.group-header {
    padding-bottom: 1rem;
    border-bottom: 2px solid #dee2e6;
    margin-bottom: 2rem;
}

.group-header h2 {
    font-size: 1.75rem;
    font-weight: 600;
    color: #2c3e50;
}

.endpoint-card {
    background: #ffffff;
    border: 1px solid #dee2e6;
    border-radius: 10px;
    padding: 1.75rem;
    margin-bottom: 1.5rem;
    transition: all 0.3s ease;
    scroll-margin-top: 100px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.endpoint-card:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
    border-color: #007bff;
    transform: translateY(-2px);
}

.endpoint-header {
    padding-bottom: 1rem;
    border-bottom: 1px solid #f0f0f0;
    margin-bottom: 1rem;
}

.endpoint-path {
    font-size: 1rem;
    font-weight: 500;
    color: #007bff;
    background: #f8f9fa;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
}

.endpoint-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2c3e50;
    margin-top: 0.5rem;
}

.endpoint-description {
    color: #6c757d;
    font-size: 0.95rem;
    line-height: 1.6;
}

.method-badge {
    font-size: 0.75rem;
    padding: 0.35rem 0.65rem;
    font-weight: 600;
    letter-spacing: 0.5px;
}

.endpoint-content {
    padding-top: 1rem;
}

.section-title {
    font-size: 0.95rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
}

.section-title i {
    color: #007bff;
}

.code-block {
    background: #ffffff;
    border: 1px solid #dee2e6;
    border-left: 4px solid #007bff;
    border-radius: 6px;
    padding: 1rem;
    max-height: 400px;
    overflow-y: auto;
    margin: 0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.code-block code {
    font-size: 0.875rem;
    line-height: 1.6;
    color: #2c3e50;
    font-family: 'Courier New', monospace;
}

.table {
    font-size: 0.9rem;
    background: #ffffff;
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.table thead th {
    background-color: #f8f9fa;
    font-weight: 600;
    color: #495057;
    border-bottom: 2px solid #dee2e6;
}

.table tbody tr {
    background-color: #ffffff;
}

.table tbody tr:hover {
    background-color: #f8f9fa;
}

.param-name {
    color: #007bff;
    font-weight: 500;
}

.toc-wrapper {
    position: relative;
    height: fit-content;
}

.toc-card {
    border: 2px solid #dee2e6 !important;
    border-radius: 8px !important;
    background: #ffffff !important;
    margin-bottom: 0;
}

.documentation-card {
    border: 1px solid #dee2e6 !important;
    border-radius: 8px !important;
    background: #ffffff !important;
}

.sticky-wrapper {
    position: relative;
    max-height: calc(100vh - 120px);
    overflow-y: auto;
    overflow-x: hidden;
    z-index: 10;
    transition: left 0.2s ease, width 0.2s ease;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08) !important;
}

@media (min-width: 992px) {
    .sticky-wrapper {
        position: fixed;
        top: 100px;
    }
}

@media (max-width: 991px) {
    .sticky-wrapper {
        position: relative !important;
        left: auto !important;
        width: 100% !important;
        max-width: 100%;
    }
}

.toc-sidebar {
    max-height: calc(100vh - 40px);
    overflow-y: auto;
}

.toc-group-title {
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    padding-bottom: 0.5rem;
    margin-bottom: 0.75rem;
    border-bottom: 1px solid #e9ecef;
}

.toc-link {
    color: #495057;
    text-decoration: none;
    font-size: 0.8rem;
    padding: 0.5rem 0.75rem;
    border-radius: 4px;
    display: flex;
    align-items: center;
    transition: all 0.2s ease;
    margin-bottom: 0;
    border-left: 2px solid transparent;
    line-height: 1.4;
}

.toc-link:hover {
    background-color: #f8f9fa;
    color: #007bff;
    text-decoration: none;
    border-left-color: #007bff;
    padding-left: 1rem;
}

.toc-link:active,
.toc-link:focus {
    background-color: #e7f3ff;
    color: #0056b3;
    border-left-color: #0056b3;
}

.toc-link .badge-sm {
    font-size: 0.65rem;
    padding: 0.2rem 0.4rem;
    min-width: 45px;
    text-align: center;
}

.endpoint-name {
    flex: 1;
    word-break: break-word;
}

/* Custom scrollbar for code blocks */
.code-block::-webkit-scrollbar {
    width: 8px;
    height: 8px;
}

.code-block::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
}

.code-block::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
}

.code-block::-webkit-scrollbar-thumb:hover {
    background: #555;
}

/* Custom scrollbar for TOC */
.toc-sidebar::-webkit-scrollbar {
    width: 6px;
}

.toc-sidebar::-webkit-scrollbar-track {
    background: transparent;
}

.toc-sidebar::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 10px;
}

.toc-sidebar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.3);
}
</style>
